import { useEffect } from "react";
import { environment } from "../environments/environment";

/** Baked into the bundle at webpack build (see craco.config.js). */
const BUNDLE_VERSION = process.env.APP_VERSION;

/** Emitted beside index.html on every production webpack build (see craco WriteBuildMetaJson). */
const BUNDLE_BUILD_RUN_ID = process.env.REACT_APP_BUILD_RUN_ID;

/** Legacy cache-bust param from older Hub builds; stripped on load. */
export const APP_CACHE_BUST_PARAM = "_appcb";

const RELOAD_LOOP_LIMIT = 3;

export function hasAppCacheBustInUrl(href: string = window.location.href): boolean {
  try {
    return new URL(href).searchParams.has(APP_CACHE_BUST_PARAM);
  } catch {
    return false;
  }
}

/** Remove legacy ?_appcb=… from the address bar without reloading (BrowserRouter-safe). */
export function stripAppCacheBustFromUrl(href: string = window.location.href): void {
  try {
    const url = new URL(href);
    if (!url.searchParams.has(APP_CACHE_BUST_PARAM)) return;
    url.searchParams.delete(APP_CACHE_BUST_PARAM);
    const qs = url.searchParams.toString();
    const next = url.pathname + (qs ? `?${qs}` : "") + url.hash;
    window.history.replaceState(null, "", next);
  } catch {
    // ignore
  }
}

/** Parse BUILD_RUN_ID from runtime env.js body (network fetch bypasses script-tag cache). */
export function parseBuildRunIdFromEnvJs(text: string): string | null {
  const match = text.match(/BUILD_RUN_ID:\s*"([^"]*)"/);
  const id = match?.[1]?.trim();
  return id || null;
}

/** True when a freshly fetched env.js belongs to a different webpack build than this bundle. */
export function buildRunIdsMismatch(
  bundleBuildRunId: string | undefined,
  serverBuildRunId: string | null | undefined,
): boolean {
  if (!bundleBuildRunId || !serverBuildRunId) return false;
  return serverBuildRunId !== bundleBuildRunId;
}

/** True when the deployed APP_VERSION differs from the version baked into this bundle. */
export function appVersionsMismatch(
  bundleVersion: string | undefined,
  serverVersion: string | null | undefined,
): boolean {
  if (!bundleVersion || !serverVersion) return false;
  return serverVersion !== bundleVersion;
}

function shouldBlockReload(loopKey: string): boolean {
  const n = Number(sessionStorage.getItem(loopKey) || "0");
  return n >= RELOAD_LOOP_LIMIT;
}

function recordReloadAttempt(loopKey: string): void {
  const n = Number(sessionStorage.getItem(loopKey) || "0");
  sessionStorage.setItem(loopKey, String(n + 1));
}

/** Force a navigation that bypasses cached index.html / env.js (BrowserRouter-safe). */
export function forceHardReloadForNewBuild(): void {
  const url = new URL(window.location.href);
  url.searchParams.set(APP_CACHE_BUST_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

function reloadForBuildRunMismatch(serverBuildRunId: string): boolean {
  const loopKey = `app_build_reload_${serverBuildRunId}`;
  if (shouldBlockReload(loopKey)) return false;
  recordReloadAttempt(loopKey);
  forceHardReloadForNewBuild();
  return true;
}

function reloadForVersionMismatch(serverVersion: string): boolean {
  const loopKey = `app_version_reload_${serverVersion}`;
  if (shouldBlockReload(loopKey)) return false;
  recordReloadAttempt(loopKey);
  forceHardReloadForNewBuild();
  return true;
}

/**
 * Compares this bundle's build run id to BUILD_RUN_ID from the already-loaded env.js.
 * Returns true when a reload was triggered.
 */
export function reloadIfStaleBundleSync(): boolean {
  if (!BUNDLE_BUILD_RUN_ID) return false;
  if (process.env.NODE_ENV !== "production") return false;

  const serverBuildRunId = environment.buildRunId;
  if (!buildRunIdsMismatch(BUNDLE_BUILD_RUN_ID, serverBuildRunId)) return false;

  return reloadForBuildRunMismatch(serverBuildRunId!);
}

/**
 * Fetches env.js and /version with cache: "no-store" so a cached script tag cannot
 * hide a newer deploy. Returns true when a reload was triggered.
 */
export async function reloadIfStaleBundleFromNetwork(): Promise<boolean> {
  if (process.env.NODE_ENV !== "production") return false;

  if (BUNDLE_BUILD_RUN_ID) {
    try {
      const res = await fetch(`/env.js?_t=${Date.now()}`, { cache: "no-store" });
      if (res.ok) {
        const text = await res.text();
        const serverBuildRunId = parseBuildRunIdFromEnvJs(text);
        if (buildRunIdsMismatch(BUNDLE_BUILD_RUN_ID, serverBuildRunId)) {
          return reloadForBuildRunMismatch(serverBuildRunId!);
        }
      }
    } catch {
      // Network error — fall through to /version check
    }
  }

  if (!BUNDLE_VERSION) return false;

  try {
    const res = await fetch(`/version?_t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return false;
    const data: { version?: string } = await res.json();
    if (appVersionsMismatch(BUNDLE_VERSION, data.version)) {
      return reloadForVersionMismatch(data.version!);
    }
  } catch {
    // Network error — silently ignore
  }

  return false;
}

/**
 * Run before React renders so users never interact with a stale cached bundle.
 * The inline script in index.html handles the earliest mismatch; this covers
 * env.js script-tag cache and /version drift.
 */
export async function ensureFreshBundleBeforeRender(): Promise<void> {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV !== "production") return;

  if (hasAppCacheBustInUrl()) {
    stripAppCacheBustFromUrl();
  }

  if (reloadIfStaleBundleSync()) return;

  await reloadIfStaleBundleFromNetwork();
}

function checkBuildRunId(): void {
  reloadIfStaleBundleSync();
}

async function checkSemverVersion(): Promise<void> {
  await reloadIfStaleBundleFromNetwork();
}

export function useAppVersionCheck(): void {
  useEffect(() => {
    if (hasAppCacheBustInUrl()) {
      stripAppCacheBustFromUrl();
    }

    const run = () => {
      checkBuildRunId();
      void checkSemverVersion();
    };
    run();

    let lastPoll = Date.now();
    const poll = () => {
      const now = Date.now();
      if (now - lastPoll < 5 * 60_000) return;
      lastPoll = now;
      run();
    };

    let lastVisibleCheck = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastVisibleCheck < 60_000) return;
      lastVisibleCheck = now;
      run();
    };

    document.addEventListener("visibilitychange", onVisible);
    const intervalId = window.setInterval(poll, 5 * 60_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(intervalId);
    };
  }, []);
}
